import * as React from "react";
import { useState, useEffect, useContext, useCallback } from "react";
import {
  AppBar,
  Typography,
  Button,
  Box,
  Breadcrumbs,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import { PageDataContext } from "../../utils/common/PageDataContext";
import ViewDataGridGrn from "../../components/grid/ViewDataGridGrn";
import { ViewInitiatorGrnData } from "../../components/grid/GridDataGrn";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FilterGrnSearch from "../../components/grn/FilterGrnSearch";
import { externalgetApi } from "../../services/externalApis";
import { useSelector } from "react-redux";
import { GRN_API_02, GRN_API_02_2 } from "../../api/ApiModel";
import { CON_YES } from "../../utils/sys/Constants";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { FormatDate } from "../../utils/common/DateUtils";
import { AnyForNow, GrnFilterState } from "./types";
import { BUTTON_LABELS, GRN_BREADCRUMBS, PAGE_TITLES } from "../../utils/sys/GrnLabels";
import { GRN_ROUTES } from "../../utils/sys/GrnRoutes";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import {
  GRN_INITIATOR_FILTER,
  GRN_RETURN_STATUS,
  GRN_STATUS,
  GRN_STORAGE_KEYS,
  GRN_VIEW_DETAIL,
  PARAM_ID,
} from "../../utils/sys/GrnConstants";
import {
  createInitialFilters,
  useGrnStatusTypes,
  useWarehouseOptions,
} from "./hooks/useGrnFilters";
import ViewDataGridGrnInitiator from "../../components/grid/ViewDataGridGrnInitiator";

const ViewInitiatorGrn: React.FC = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = useContext(PageDataContext);
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { STAFFMAPLOCODE, USERNAME, COMPCODE } = SYS_USER.USER_PARAM || {};
  const [allData, setAllData] = useState<AnyForNow[]>([]);
  const [allfullData, setAllfullData] = useState<AnyForNow[]>([]);
  const [filteredRows, setFilteredRows] = useState<AnyForNow[]>([]);
  const [filteredfullRows, setFilteredfullRows] = useState<AnyForNow[]>([]);
  const moduleParamJson = useSelector((state: AnyForNow) => state.moduleParamJson);
  const moduleParamJsonArray = Array.isArray(moduleParamJson)
    ? moduleParamJson
    : Object.values(moduleParamJson || {});
  const shouldIncludeAll = moduleParamJsonArray.some(
    (item) => item.PARAID === PARAM_ID.APPROVAL_WORKFLOW && item.PARAVALUE === CON_YES
  );
  const warehouseOptions = useWarehouseOptions(COMPCODE);
  const excludedStatuses = GRN_INITIATOR_FILTER.EXCLUDED_STATUSES as readonly string[];
  const mainStatusesWithApproval =
    GRN_INITIATOR_FILTER.MAIN_STATUSES_WITH_APPROVAL as readonly string[];
  const mainStatusesDefault =
    GRN_INITIATOR_FILTER.MAIN_STATUSES_DEFAULT as readonly string[];
  const editableStatuses = [
    GRN_STATUS.SAVED,
    GRN_RETURN_STATUS.RETURNED,
    GRN_STATUS.REVERSE,
  ] as readonly string[];

  const statusFilter = useCallback(
    (item: AnyForNow) => {
      if (String(item.trcatcode) !== GRN_INITIATOR_FILTER.TRCATCODE) {
        return false;
      }
      if (shouldIncludeAll) {
        return true;
      }
      return !excludedStatuses.includes(String(item.trsts));
    },
    [excludedStatuses, shouldIncludeAll]
  );
  const { statusTypes: grnStatusTypes } = useGrnStatusTypes({
    filter: statusFilter,
    deps: [statusFilter],
  });
  const [filters, setFilters] = useState<GrnFilterState>(createInitialFilters());
  useEffect(() => {
    setPageData({
      title: PAGE_TITLES.GRN_MANAGEMENT,
      // icon: <HomeIcon />,
      // showBackButton: false,
      // customActions: <button>Refresh</button>,
      // breadcrumbs: ["Home", "Dashboard"],
    });
  }, [setPageData]);

  const handleItemClick = (grncode: string, trsts: string, pocode: string, actionText: string): void => {
    if (actionText === BUTTON_LABELS.VIEW) {
      navigate(GRN_ROUTES.VIEW_DETAIL, {
        state: { grncode, trsts, origin: GRN_INITIATOR_FILTER.ORIGIN },
      });
    } else if (actionText === BUTTON_LABELS.EDIT) {
      navigate(GRN_ROUTES.EDIT, { state: { grncode, trsts, pocode ,origin: GRN_VIEW_DETAIL.ORIGIN_EDIT} });
    }
  };

const [loading, setLoading] = useState<boolean>(true);
const hasFetchedGrnListRef = React.useRef(false);
const grnStatusTypesRef = React.useRef<AnyForNow[]>([]);

// Update ref when grnStatusTypes changes
React.useEffect(() => {
  grnStatusTypesRef.current = grnStatusTypes;
}, [grnStatusTypes]);

// Optimized: Single API call that processes data for both filtered and full lists
const fetchGrnListData = useCallback(async (): Promise<void> => {
  if (!COMPCODE || !STAFFMAPLOCODE) {
    return;
  }

  // Prevent duplicate calls
  if (hasFetchedGrnListRef.current) {
    return;
  }

  try {
    hasFetchedGrnListRef.current = true;
    // Single API call instead of two duplicate calls
      const result = await externalgetApi(GRN_API_02_2, "GET", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
       
      });

    if (!Array.isArray(result) || result.length === 0) {
      setAllData([]);
      setAllfullData([]);
      setFilteredRows([]);
      setLoading(false);
      return;
    }

    const shouldIncludeAll =
      moduleParamJsonArray.filter((item) => item.PARAID === PARAM_ID.APPROVAL_WORKFLOW)[0]
        ?.PARAVALUE === CON_YES;

    // Process data once and create both filtered versions
    const processedData = result.map((item, index) => {
      const matchedStatus = grnStatusTypesRef.current.find(
        (status) => String(status.trsts) === String(item.trsts)
      );

      return {
        ...item,
        id: item.grncode || index,
        invoicedate: FormatDate(item.invoicedate),
        endate: FormatDate(item.endate),
        statusLabel: matchedStatus?.trenuserremark || GRN_BREADCRUMBS.STATUS_FALLBACK,
        statusColor:
          matchedStatus?.colorcode || GRN_INITIATOR_FILTER.STATUS_FALLBACK_COLOR,
        invoicedateRaw: item.invoicedate,
        endateRaw: item.endate,
        pocode: item.pocode,
        grnstatuscode: item.trsts,
      };
    });

    // Filter for main list (allData) - stricter filter
    const filteredForMain = processedData.filter((item) => {
      if (shouldIncludeAll) {
        return mainStatusesWithApproval.includes(String(item.trsts));
      } else {
        return mainStatusesDefault.includes(String(item.trsts));
      }
    });

    // Filter for full list (allfullData) - more inclusive filter
    const filteredForFull = processedData.filter((item) => {
      if (shouldIncludeAll) {
        return String(item.trsts); // Include all
      } else {
        return !excludedStatuses.includes(String(item.trsts));
      }
    });

    setAllData(filteredForMain);
    setAllfullData(filteredForFull);
    setFilteredRows(filteredForMain);
  } catch (err) {
    console.error("Error fetching GRN list data:", err);
    hasFetchedGrnListRef.current = false; // Reset on error to allow retry
  } finally {
    setLoading(false);
  }
}, [COMPCODE, STAFFMAPLOCODE, moduleParamJsonArray]);

useEffect(() => {
  // Only fetch when status types are loaded (needed for status matching) and we haven't fetched yet
  if (grnStatusTypes.length > 0 && !hasFetchedGrnListRef.current && COMPCODE && STAFFMAPLOCODE) {
    fetchGrnListData();
  }
}, [fetchGrnListData, grnStatusTypes.length, COMPCODE, STAFFMAPLOCODE]);

const isFilterApplied = Object.values(filters).some(
  (val) => val !== null && val !== "" 
);


useEffect(() => {
  if (!allfullData.length) {
    setFilteredfullRows([]);
    return;
  }

  let filtered = allfullData;

  if (filters.grnId) {
    const search = filters.grnId.toLowerCase();
    filtered = filtered.filter((item) =>
      item.grncode.toString().toLowerCase().includes(search)
    );
  }

if (filters.warehouse) {
  filtered = filtered.filter(
    (item) => String(item.restcode) === String(filters.warehouse)
  );
}


  if (filters.status) {
    filtered = filtered.filter(
      (item) => String(item.grnstatuscode) === String(filters.status)
    );
  }

 if (filters.fromDate && filters.toDate) {
  const from = new Date(filters.fromDate);
  const to = new Date(filters.toDate);

  const start = from < to ? from : to;
  const end = from < to ? to : from;

  filtered = filtered.filter(
    (item) => {
      const date = new Date(item.invoicedateRaw || item.endateRaw);
      return date >= start && date <= end;
    }
  );
} else if (filters.fromDate) {
  const from = new Date(filters.fromDate);
  filtered = filtered.filter(
    (item) => new Date(item.invoicedateRaw) >= from
  );
} else if (filters.toDate) {
  const to = new Date(filters.toDate);
  filtered = filtered.filter(
    (item) => new Date(item.endateRaw) <= to
  );
}


  setFilteredfullRows(filtered);
}, [filters, allfullData]);

  const handleFilterChange = (newFilters: GrnFilterState): void => {
    setFilters(newFilters);
  };

const menuItems = [
  {
    text: BUTTON_LABELS.VIEW,
    icon: <VisibilityOutlinedIcon />,
    onClick: (row: AnyForNow) =>
      handleItemClick(row.grncode, row.trsts, row.pocode, BUTTON_LABELS.VIEW),
  },
];

const menuEditItems = [
 
  {
    text: BUTTON_LABELS.PROCEED,
    icon: <ArrowCircleRightIcon />,
    onClick: (row: AnyForNow) =>
      handleItemClick(row.grncode, row.trsts, row.pocode, BUTTON_LABELS.EDIT),
  },
];

  const toCreateGrn = (): void => {
    localStorage.removeItem(GRN_STORAGE_KEYS.GRN_PAGE_DATA);
    navigate(GRN_ROUTES.CREATE);
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator={GRN_BREADCRUMBS.SEPARATOR}
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
            // href=""
          >
            {GRN_BREADCRUMBS.HOME}
          </Link>
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
            // href=""
          >
             {GRN_BREADCRUMBS.MANAGEMENT}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            // href="#"
            aria-current="page"
          >
           {GRN_BREADCRUMBS.GRN_LIST} 
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.vg_appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {ViewInitiatorGrnData.name}
              </Typography>
            </Grid>

            <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
              <Box
                sx={{
                  gap: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                {/* <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ ...styles.btn_primary }}
                >
                  View
                </Button> */}
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  size="medium"
                  // sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                  sx={{ ...styles.btn_primary }}
                  onClick={toCreateGrn}
                >
                  {BUTTON_LABELS.CREATE}
                </Button>
              </Box>
            </Grid>
            {/* <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.vg_grid_new_button}
            >
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={goToCreateGrn}
              >
                New
              </Button>
            </Grid> */}
          </Grid>
        </AppBar>
        {/* <FilterGrnSearch /> */}
        <FilterGrnSearch
          warehouseOptions={warehouseOptions}
          grnStatusTypes={grnStatusTypes}
          onFilterChange={handleFilterChange}
        />

        <Box sx={styles.view_page_container}>
          <ViewDataGridGrnInitiator
  columns={ViewInitiatorGrnData.columns}
  rows={isFilterApplied ? filteredfullRows : filteredRows}
  menuItems={menuItems}

  getMenuItems={(row: AnyForNow) =>
    editableStatuses.includes(String(row.trsts)) ? menuEditItems : menuItems
  }
  tabletitle={ViewInitiatorGrnData.name}
  gridid={GRN_INITIATOR_FILTER.GRID_ID}
  sx={styles.view_data_grid_page}
  grnStatusTypes={grnStatusTypes}
  onSelectionChange={undefined}
  grnReturnStatusTypes={undefined}
  selectionModel={undefined}
  loading={loading}
  showCheckbox={false}
/>

        </Box>
      </Box>
    </>
  );
};

export default ViewInitiatorGrn;
